import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Archive, ArchiveRestore, Trash2, Loader2, AlertTriangle, PauseCircle, PlayCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { money } from "@/lib/format";
import { Button } from "@/components/ui-kit/Button";
import { Input } from "@/components/ui-kit/Input";
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui-kit/Dialog";

/**
 * Archive / Reinstate / guarded permanent Delete for a single client.
 * `variant="row"` renders compact icon buttons for a table cell; `variant="header"`
 * renders labelled buttons (and the "archive instead" note) for the overview header.
 */
export function ClientActions({
  client,
  canHardDelete,
  outstanding = 0,
  openMatterCount = 0,
  variant = "row",
  onChanged,
  afterDelete,
}) {
  const navigate = useNavigate();
  const [mode, setMode] = useState(null); // "archive" | "reinstate" | "delete"
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const archived = Boolean(client.archived);
  const onHold = Boolean(client.on_hold);
  const isHeader = variant === "header";

  const close = () => {
    if (busy) return;
    setMode(null);
    setError("");
    setConfirmText("");
  };

  const run = async (fn, after) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      close();
      after?.();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const doArchive = () => run(() => api.post(`/clients/${client.id}/archive`), onChanged);
  const doReinstate = () => run(() => api.post(`/clients/${client.id}/reinstate`), onChanged);
  const doHold = () => run(() => api.post(`/clients/${client.id}/hold`), onChanged);
  const doUnhold = () => run(() => api.post(`/clients/${client.id}/unhold`), onChanged);
  const doDelete = () =>
    run(() => api.delete(`/clients/${client.id}`), () => (afterDelete ? afterDelete() : onChanged?.()));

  return (
    <>
      <div className={isHeader ? "flex items-center gap-2" : "flex items-center justify-end gap-1"}>
        {archived ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setMode("reinstate")}
            data-testid={`reinstate-client-${client.id}`}
            title="Reinstate"
          >
            <ArchiveRestore className="h-4 w-4" /> {isHeader && "Reinstate"}
          </Button>
        ) : (
          <>
            {onHold ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setMode("unhold")}
                data-testid={`unhold-client-${client.id}`}
                title="Set active"
              >
                <PlayCircle className="h-4 w-4" /> {isHeader && "Set active"}
              </Button>
            ) : (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setMode("hold")}
                data-testid={`hold-client-${client.id}`}
                title="Put on hold"
              >
                <PauseCircle className="h-4 w-4" /> {isHeader && "Put on hold"}
              </Button>
            )}
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setMode("archive")}
              data-testid={`archive-client-${client.id}`}
              title="Archive"
            >
              <Archive className="h-4 w-4" /> {isHeader && "Archive"}
            </Button>
          </>
        )}

        {canHardDelete ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setMode("delete")}
            data-testid={`delete-client-${client.id}`}
            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            title="Delete permanently"
          >
            <Trash2 className="h-4 w-4" /> {isHeader && "Delete"}
          </Button>
        ) : (
          isHeader && (
            <span className="text-xs text-muted-foreground" data-testid={`delete-blocked-note-${client.id}`}>
              Has billing history — archive instead.
            </span>
          )
        )}
      </div>

      {/* Archive — light confirm, warns on open matter / outstanding balance */}
      <Dialog open={mode === "archive"} onOpenChange={(o) => !o && close()}>
        <DialogContent data-testid="archive-confirm-dialog">
          <DialogTitle className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-md bg-status-superseded/10 text-status-superseded"><Archive className="h-4 w-4" /></span>
            Archive {client.name}?
          </DialogTitle>
          <DialogDescription>
            They&apos;ll move to the Archived list and leave your active clients, Workbench, and dashboard. All their data is preserved.
          </DialogDescription>
          {(openMatterCount > 0 || outstanding > 0) && (
            <div className="mt-3 space-y-2 rounded-md border border-status-partial/30 bg-status-partial/10 p-3 text-sm" data-testid="archive-warning">
              {openMatterCount > 0 && (
                <p className="flex items-start gap-2 text-foreground">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-status-partial" />
                  {openMatterCount === 1 ? "Their open matter will be closed" : `Their ${openMatterCount} open matters will be closed`} as part of archiving (data preserved).
                </p>
              )}
              {outstanding > 0 && (
                <p className="flex items-start gap-2 text-foreground">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-status-partial" />
                  This client still has an outstanding balance of <span className="nums font-semibold">{money(outstanding)}</span>. Archiving stops collection here — only do this if you&apos;re done collecting.
                </p>
              )}
            </div>
          )}
          {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button variant="ghost" onClick={close} disabled={busy}>Cancel</Button>
            <Button onClick={doArchive} disabled={busy} data-testid="archive-confirm-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />} Archive client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reinstate — light confirm */}
      <Dialog open={mode === "reinstate"} onOpenChange={(o) => !o && close()}>
        <DialogContent data-testid="reinstate-confirm-dialog">
          <DialogTitle className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-md bg-status-paid/10 text-status-paid"><ArchiveRestore className="h-4 w-4" /></span>
            Reinstate {client.name}?
          </DialogTitle>
          <DialogDescription>
            They return to your active clients with no active matter — their old matters stay closed in history. Create a new matter for their new issue.
          </DialogDescription>
          {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button variant="ghost" onClick={close} disabled={busy}>Cancel</Button>
            <Button onClick={doReinstate} disabled={busy} data-testid="reinstate-confirm-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArchiveRestore className="h-4 w-4" />} Reinstate client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Put on hold — light confirm */}
      <Dialog open={mode === "hold"} onOpenChange={(o) => !o && close()}>
        <DialogContent data-testid="hold-confirm-dialog">
          <DialogTitle className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-md bg-primary/10 text-primary"><PauseCircle className="h-4 w-4" /></span>
            Put {client.name} on hold?
          </DialogTitle>
          <DialogDescription>
            They&apos;ll move off your active list and the Workbench, and drop out of proactive work alerts. Their matter stays open and fully billable — you can still log time, invoice, and collect. Overdue invoices still alert.
          </DialogDescription>
          {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button variant="ghost" onClick={close} disabled={busy}>Cancel</Button>
            <Button onClick={doHold} disabled={busy} data-testid="hold-confirm-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PauseCircle className="h-4 w-4" />} Put on hold
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set active (from hold) — light confirm */}
      <Dialog open={mode === "unhold"} onOpenChange={(o) => !o && close()}>
        <DialogContent data-testid="unhold-confirm-dialog">
          <DialogTitle className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-md bg-status-paid/10 text-status-paid"><PlayCircle className="h-4 w-4" /></span>
            Set {client.name} active?
          </DialogTitle>
          <DialogDescription>
            They return to your active clients list and the Workbench, and proactive work alerts resume.
          </DialogDescription>
          {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button variant="ghost" onClick={close} disabled={busy}>Cancel</Button>
            <Button onClick={doUnhold} disabled={busy} data-testid="unhold-confirm-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />} Set active
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete — strong, permanent confirm requiring typed confirmation */}
      <Dialog open={mode === "delete"} onOpenChange={(o) => !o && close()}>
        <DialogContent data-testid="delete-confirm-dialog">
          <DialogTitle className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-md bg-destructive/10 text-destructive"><Trash2 className="h-4 w-4" /></span>
            Permanently delete {client.name}?
          </DialogTitle>
          <DialogDescription>
            This <span className="font-semibold text-destructive">cannot be undone</span>. The client and their matters/drafts will be erased. This is only allowed because they have no billing history. The deletion is recorded in the audit log.
          </DialogDescription>
          <div className="mt-3">
            <p className="mb-1.5 text-sm text-muted-foreground">Type <span className="font-semibold text-foreground">DELETE</span> to confirm.</p>
            <Input
              data-testid="delete-confirm-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              autoFocus
            />
          </div>
          {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button variant="ghost" onClick={close} disabled={busy}>Cancel</Button>
            <Button
              onClick={doDelete}
              disabled={busy || confirmText !== "DELETE"}
              data-testid="delete-confirm-button"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
